import React, { useState, useEffect } from 'react';
import { TrendingUp, ArrowRight, Calculator, AlertCircle, Zap } from 'lucide-react';
import { formatCurrency, formatNumber, formatPercentage, getPercentageColor, formatIDR, formatCryptoIDR, USD_TO_IDR } from '../utils/formatters';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';

const Trading = () => {
  const [selectedCoin, setSelectedCoin] = useState('BTC');
  const [orderType, setOrderType] = useState('market');
  const [tradeDirection, setTradeDirection] = useState('buy');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [buyExchange, setBuyExchange] = useState('MEXC');
  const [sellExchange, setSellExchange] = useState('Binance');
  
  const [marketData, setMarketData] = useState({
    BTC: {
      MEXC: { bid: 75388, ask: 75390, volume: 125.5 },
      Binance: { bid: 76920, ask: 76925, volume: 234.8 },
      'Gate.io': { bid: 75500, ask: 75505, volume: 89.3 },
      Indodax: { bid: 75450, ask: 75455, volume: 45.6 },
    },
    ETH: {
      MEXC: { bid: 3800, ask: 3802, volume: 1250 },
      Binance: { bid: 3850, ask: 3852, volume: 2340 },
      'Gate.io': { bid: 3876, ask: 3878, volume: 890 },
      Indodax: { bid: 3810, ask: 3812, volume: 456 },
    },
    BNB: {
      MEXC: { bid: 658, ask: 658.5, volume: 380 },
      Binance: { bid: 655, ask: 655.5, volume: 520 },
      'Gate.io': { bid: 650, ask: 650.5, volume: 290 },
      Indodax: { bid: 652, ask: 652.5, volume: 156 },
    },
  });

  const coins = Object.keys(marketData);
  const exchanges = ['MEXC', 'Binance', 'Gate.io', 'Indodax'];

  const calculateProfit = () => {
    if (!amount || !selectedCoin) return null;

    const buyPrice = marketData[selectedCoin][buyExchange].ask;
    const sellPrice = marketData[selectedCoin][sellExchange].bid;
    const qty = parseFloat(amount);

    const buyTotal = buyPrice * qty;
    const sellTotal = sellPrice * qty;
    const profit = sellTotal - buyTotal;
    const profitPercentage = ((profit / buyTotal) * 100);

    return {
      buyPrice,
      sellPrice,
      buyTotal,
      sellTotal,
      profit,
      profitPercentage,
      fees: buyTotal * 0.001 + sellTotal * 0.001, // 0.1% fee on each side
      netProfit: profit - (buyTotal * 0.001 + sellTotal * 0.001),
    };
  };

  const profitData = calculateProfit();

  const handleQuickBuy = async () => {
    if (!amount) {
      toast.error('Please enter an amount');
      return;
    }

    if (profitData && profitData.profitPercentage < 0) {
      toast.error('This trade would result in a loss!');
      return;
    }

    toast.loading('Executing trade...', { id: 'trade' });

    // Simulate trade execution
    await new Promise(resolve => setTimeout(resolve, 2000));

    toast.success(
      <div>
        <p>Trade executed successfully!</p>
        <p className="text-sm mt-1">
          Bought {amount} {selectedCoin} on {buyExchange} and sold on {sellExchange}
        </p>
        <p className="text-sm mt-1 text-green-400">
          Profit: {formatIDR(profitData.netProfit * USD_TO_IDR)}
        </p>
      </div>,
      { id: 'trade', duration: 5000 }
    );

    setAmount('');
  };

  const handleManualTrade = async (side) => {
    if (!amount) {
      toast.error('Please enter an amount');
      return;
    }

    if (orderType === 'limit' && !price) {
      toast.error('Please enter a price for limit order');
      return;
    }

    toast.loading(`Placing ${side} order...`, { id: 'order' });

    // Simulate order placement
    await new Promise(resolve => setTimeout(resolve, 1500));

    toast.success(
      <div>
        <p>{side === 'buy' ? 'Buy' : 'Sell'} order placed successfully!</p>
        <p className="text-sm mt-1">
          {amount} {selectedCoin} on {side === 'buy' ? buyExchange : sellExchange}
        </p>
      </div>,
      { id: 'order' }
    );

    setAmount('');
    setPrice('');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Trading</h1>
        <p className="text-gray-400">Execute arbitrage trades and manage orders</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trading Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Arbitrage */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <Zap className="h-5 w-5 mr-2 text-yellow-500" />
                Quick Arbitrage
              </h2>
              <span className="text-xs text-gray-400">Auto-execute best opportunities</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Coin</label>
                <select
                  value={selectedCoin}
                  onChange={(e) => setSelectedCoin(e.target.value)}
                  className="input-field"
                >
                  {coins.map(coin => (
                    <option key={coin} value={coin}>{coin}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Buy From</label>
                <select
                  value={buyExchange}
                  onChange={(e) => setBuyExchange(e.target.value)}
                  className="input-field"
                >
                  {exchanges.map(exchange => (
                    <option key={exchange} value={exchange}>{exchange}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sell To</label>
                <select
                  value={sellExchange}
                  onChange={(e) => setSellExchange(e.target.value)}
                  className="input-field"
                >
                  {exchanges.map(exchange => (
                    <option key={exchange} value={exchange}>{exchange}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input-field"
                placeholder={`Enter ${selectedCoin} amount`}
                step="0.0001"
              />
            </div>

            {profitData && amount && (
              <div className="bg-dark-bg rounded-lg p-4 mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Buy Price</span>
                  <span className="text-white">{formatCryptoIDR(profitData.buyPrice, 0)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Sell Price</span>
                  <span className="text-white">{formatCryptoIDR(profitData.sellPrice, 0)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Total Buy</span>
                  <span className="text-white">{formatIDR(profitData.buyTotal * USD_TO_IDR)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Total Sell</span>
                  <span className="text-white">{formatIDR(profitData.sellTotal * USD_TO_IDR)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Trading Fees</span>
                  <span className="text-red-500">-{formatIDR(profitData.fees * USD_TO_IDR)}</span>
                </div>
                <div className="border-t border-dark-border pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 font-medium">Net Profit</span>
                    <div className="text-right">
                      <p className={cn(
                        'font-semibold',
                        profitData.netProfit >= 0 ? 'text-green-500' : 'text-red-500'
                      )}>
                        {formatIDR(Math.abs(profitData.netProfit * USD_TO_IDR))}
                      </p>
                      <p className={cn(
                        'text-sm',
                        getPercentageColor(profitData.profitPercentage)
                      )}>
                        {formatPercentage(profitData.profitPercentage)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleQuickBuy}
              disabled={!amount || !profitData || profitData.profitPercentage < 0}
              className="w-full btn-success py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Execute Arbitrage Trade
            </button>
          </div>

          {/* Manual Trading */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Manual Trading</h2>

            <div className="space-y-4">
              <div className="flex rounded-lg bg-dark-bg p-1">
                <button
                  onClick={() => setOrderType('market')}
                  className={cn(
                    'flex-1 py-2 rounded-md text-sm font-medium transition-colors',
                    orderType === 'market'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  )}
                >
                  Market Order
                </button>
                <button
                  onClick={() => setOrderType('limit')}
                  className={cn(
                    'flex-1 py-2 rounded-md text-sm font-medium transition-colors',
                    orderType === 'limit'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  )}
                >
                  Limit Order
                </button>
              </div>

              {orderType === 'limit' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Limit Price
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="input-field"
                    placeholder="Enter price"
                    step="0.01"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleManualTrade('buy')}
                  className="btn-success py-3"
                >
                  Buy {selectedCoin}
                </button>
                <button
                  onClick={() => handleManualTrade('sell')}
                  className="btn-danger py-3"
                >
                  Sell {selectedCoin}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Market Overview */}
        <div className="space-y-6">
          {/* Best Opportunities */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Best Opportunities</h3>
            <div className="space-y-3">
              {coins.map(coin => {
                let bestBuy = { exchange: '', price: Infinity };
                let bestSell = { exchange: '', price: 0 };

                exchanges.forEach(exchange => {
                  if (marketData[coin][exchange].ask < bestBuy.price) {
                    bestBuy = { exchange, price: marketData[coin][exchange].ask };
                  }
                  if (marketData[coin][exchange].bid > bestSell.price) {
                    bestSell = { exchange, price: marketData[coin][exchange].bid };
                  }
                });

                const profit = ((bestSell.price - bestBuy.price) / bestBuy.price) * 100;

                return (
                  <div key={coin} className="bg-dark-bg rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{coin}</span>
                      <span className={cn(
                        'text-sm font-medium',
                        getPercentageColor(profit)
                      )}>
                        {formatPercentage(profit)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{bestBuy.exchange}</span>
                      <ArrowRight className="h-3 w-3" />
                      <span>{bestSell.exchange}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Market Prices */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Market Prices</h3>
            <div className="space-y-4">
              {exchanges.map(exchange => (
                <div key={exchange}>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">{exchange}</h4>
                  <div className="space-y-2">
                    {coins.map(coin => (
                      <div key={coin} className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{coin}</span>
                        <div className="text-right">
                          <p className="text-white">
                            {formatCryptoIDR(marketData[coin][exchange].bid, 0)}
                          </p>
                          <p className="text-xs text-gray-500">
                            Vol: {formatNumber(marketData[coin][exchange].volume, 2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="glass-card p-4 bg-blue-500/5 border-blue-500/20">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Trading Tips</h4>
                <p className="text-xs text-gray-400">
                  Always check exchange fees and withdrawal limits before executing large trades. 
                  Market conditions can change rapidly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trading;